import React, { useMemo } from 'react';
import { useAppDispatch, useAppState } from '@/context/appState';
import useLocalCart from '@/hooks/useLocalCart';
import CartLineItem from './CartLineItem';
import { ModalBody, ModalImpl } from '../ModalFactory';
import { ModalDataTypes } from '@/context/types';
import { useModalActions } from '../useModalActions';
import { Modal } from '../modal';
import { CartAPI } from '@/helpers/api/CartAPI';
import {
  ErrorNotification,
  WarningNotification,
} from '@/components/toast/toastMessages';
import useUser from '@/hooks/useUser';
import { splitFullName } from '@/helpers/misc';
import { useNavigate } from 'react-router-dom';

type CartModalData = ModalDataTypes['CartDrawer'];

export const CartDrawer: ModalImpl = (props) => {
  return (
    <Modal variant="drawer-right" ariaLabel="Cart" {...props} className="!px-0">
      {Cart}
    </Modal>
  );
};

// ============================================================================
// Main Cart Drawer Component
// ============================================================================
const Cart: ModalBody<CartModalData> = ({ withModalClose }) => {
  const { totalItems, cart, freeShipping, lockCheckout } = useLocalCart();
  const dispatch = useAppDispatch();
  const { user } = useUser();
  const navigate = useNavigate();
  // Add a local state for the loading spinner
  const [isProcessing, setIsProcessing] = React.useState(false);

  // Handler for Checkout
  const handleCheckoutClick = withModalClose(async () => {
    if (!user) {
      const currentUrl = location.pathname + location.search;
      const encodedRedirect = encodeURIComponent(currentUrl);
      return navigate(`/login?redirect=${encodedRedirect}`);
    }
    // 1. Prevent duplicate submissions
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // 2. Format local cart items for the Storefront API
      const shopifyLines = cart.lines.map((item: any) => {
        // Base line item payload
        const linePayload: any = {
          merchandiseId: item.id, // Must be the Variant ID (gid://...)
          quantity: item.quantity,
        };

        // Check if this item is a Bundle/Box that contains sub-items
        if (item.contents && item.contents.length > 0) {
          // A. Create Human-Readable summary for packing slips and checkout UI
          // Result: "2x Vanilla Shake, 1x Coconut Bar"
          const readableContents = item.contents
            .map((subItem: any) => `${subItem.quantity}x ${subItem.title}`)
            .join(', ');

          // B. Create Machine-Readable JSON for Webhooks/Inventory scripts
          // Result: "[{id: 'gid://...', quantity: 2}, ...]"
          const rawData = JSON.stringify(
            item.contents.map((subItem: any) => ({
              id: subItem.id,
              quantity: subItem.quantity,
            })),
          );

          // Attach attributes to the Storefront API payload
          linePayload.attributes = [
            { key: 'Box Contents', value: readableContents },
            // The leading underscore tells Shopify to hide this row from the Checkout UI
            { key: '_bundle_data', value: rawData },
          ];
        }

        return linePayload;
      });

      const name = splitFullName(
        user?.fullName ?? cart?.recipient?.fullName ?? '',
      );
      // Pass the parsed address from your cart recipient state
      const payload = {
        address: {
          firstName: name.firstName,
          lastName: name.lastName,
          address1: cart.recipient?.street,
          address2: cart.recipient?.secondary_address,
          city: cart.recipient?.place,
          province: cart.recipient?.region_code,
          zip: cart.recipient?.postcode,
          country: 'United States',
        },
        recipientName: cart.recipient?.fullName,

        customer: {
          phone: user!.phone,
        },
        instructions: cart.recipient?.instructions,
        lines: shopifyLines,
      };

      if (shopifyLines.length === 0) {
        throw new Error('Cannot checkout with an empty cart.');
      }

      // 3. Hit the Shopify Storefront API
      const response = await CartAPI.createCart(payload);
      const cartData = response.data?.cartCreate;

      // 4. Catch Shopify User Errors (e.g., Out of Stock, Invalid Variant)
      if (cartData?.userErrors && cartData.userErrors.length > 0) {
        throw new Error(cartData.userErrors[0].message);
      }

      const checkoutUrl = cartData?.cart?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Failed to generate a secure checkout URL.');
      }

      dispatch({
        type: 'LOCAL_UNFINISHED_CART',
        id: cartData?.cart?.id, // Make sure your reducer handles this!
      });

      // Some Shopify setups allow you to append ?return_to= to the checkout URL.
      // (See Bulletproofing Notes below for the most reliable return method).
      const finalCheckoutUrl = `${checkoutUrl}`;

      // 6. Execute the Seamless Redirect
      window.location.href = finalCheckoutUrl;
    } catch (error: any) {
      console.error('Checkout Initialization Failed:', error);
      // 1. Extract the raw error message
      const rawMessage = error?.message?.toLowerCase() || '';

      // 2. Generate appropriate, user-friendly messages
      let friendlyMessage = 'An unexpected error occurred. Please try again.';

      if (rawMessage.includes('network') || rawMessage.includes('fetch')) {
        friendlyMessage =
          'Network disconnected. Please check your internet and try again.';
      } else if (
        rawMessage.includes('inventory') ||
        rawMessage.includes('stock')
      ) {
        friendlyMessage =
          'Some items in your box are out of stock. Please review your cart.';
      } else if (rawMessage.includes('empty')) {
        friendlyMessage =
          'Your cart is empty. Add some treats before checking out!';
      } else if (rawMessage.includes('invalid')) {
        friendlyMessage =
          'There was an issue verifying your items. Please clear your cart and try again.';
      }

      // 3. Trigger the standard Error Toast
      ErrorNotification({
        title: 'Checkout Failed',
        message: friendlyMessage,
      });
    } finally {
      // Only resets if the redirect fails. If successful, the page will unload.
      setIsProcessing(false);
    }
  });

  let subtotal = 0;

  subtotal = cart.lines.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0,
  );

  const isFreeShipping = !!freeShipping?.greaterThanOrEqualToSubtotal?.amount;
  const shippingThreshold: number = parseFloat(
    freeShipping?.greaterThanOrEqualToSubtotal?.amount ?? '-1',
  );

  const percentToFree = Math.min((subtotal / shippingThreshold) * 100, 100);
  const amountLeft = (shippingThreshold - subtotal).toFixed(2);

  return (
    <div className="relative flex flex-col h-full bg-brand-cream">
      {/* Header Section with Progress Bar */}
      <div className="flex flex-col pt-5 pb-3 px-5 border-b border-brand-truffle/10 shrink-0">
        <div className="flex justify-between items-end">
          <h2 className="font-display text-text-heading text-xl font-[700]">
            Your Box
          </h2>
          <span className="font-body text-[10px] text-text-muted uppercase tracking-widest">
            {totalItems} Items
          </span>
        </div>

        {/* Motivational Shipping Bar */}
        {isFreeShipping && (
          <div className="mt-3 w-full">
            <div className="flex justify-between text-[11px] font-body font-medium mb-1.5">
              {percentToFree >= 100 ? (
                <span className="text-brand-sage font-bold">
                  🎉 You've unlocked Free Shipping!
                </span>
              ) : (
                <span className="text-text-muted">
                  Add{' '}
                  <span className="font-bold text-brand-burgundy">
                    ${amountLeft}
                  </span>{' '}
                  for Free Shipping
                </span>
              )}
            </div>
            <div className="w-full h-1.5 bg-brand-truffle/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-burgundy transition-all duration-500 ease-out"
                style={{ width: `${percentToFree}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Compressed Scrollable Main Content */}
      <div className="flex-1 overflow-y-auto px-5 py-0 hide-scrollbar">
        {cart?.lines?.map((item) => (
          <CartLineItem withModalClose={withModalClose} id={item.id} />
        ))}
      </div>

      {/* Dominant Sticky Footer */}
      <div className="sticky bottom-0 left-0 w-full bg-white border-t border-brand-truffle/10 p-5 pb-safe shadow-[0_-10px_20px_-10px_rgba(0,0,0,0.08)] z-50">
        <div className="flex justify-between items-end mb-4">
          <span className="font-body text-[11px] text-text-muted uppercase tracking-widest">
            Subtotal
          </span>
          <span className="font-display font-bold text-2xl text-text-heading">
            ${subtotal.toFixed(2)}
          </span>
        </div>

        <button
          onClick={handleCheckoutClick}
          disabled={totalItems === 0 || isProcessing || lockCheckout}
          className="w-full py-4 rounded-full font-display font-bold text-[16px] tracking-wide bg-brand-truffle text-brand-cream shadow-lift hover:bg-brand-truffle/90 transition-all duration-300 disabled:opacity-50"
        >
          {isProcessing ? 'Securely Loading...' : 'Checkout Now'}
        </button>

        {/* Trust Signals */}
        <div className="flex items-center justify-center gap-1.5 mt-3 opacity-60">
          <svg
            className="w-3 h-3 text-text-main"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
          </svg>
          <span className="text-[10px] font-body uppercase tracking-wider text-text-main font-semibold">
            Secure Encrypted Checkout
          </span>
        </div>
      </div>
    </div>
  );
};
